import api from './api';
import { requestLaudoPresignDownload, uploadBlobToLaudo } from './laudoFiles';

function normalizeWhatsappPhone(rawPhone) {
  if (!rawPhone) return '';

  let digits = String(rawPhone).replace(/[^0-9]/g, '');

  if (digits.length === 12 && digits.charAt(0) === '0') {
    digits = digits.substring(1);
  }

  if (digits.length === 10 || digits.length === 11) {
    return `+55${digits}`;
  }

  if (
    (digits.length === 12 || digits.length === 13) &&
    digits.startsWith('55')
  ) {
    return `+${digits}`;
  }

  return '';
}

function normalizePdfBase64Input(pdfBase64) {
  if (typeof pdfBase64 !== 'string') {
    throw new Error('PDF base64 inválido');
  }

  const trimmedValue = pdfBase64.trim();
  const base64StartIndex = trimmedValue.indexOf('base64,');
  const withoutDataUrl =
    base64StartIndex >= 0
      ? trimmedValue.substring(base64StartIndex + 'base64,'.length)
      : trimmedValue;

  let normalized = withoutDataUrl.replace(/\s+/g, '');

  if (normalized.includes('-') || normalized.includes('_')) {
    normalized = normalized.replace(/-/g, '+').replace(/_/g, '/');
  }

  const padding = normalized.length % 4;
  if (padding > 0) {
    normalized = normalized.padEnd(normalized.length + (4 - padding), '=');
  }

  if (!normalized) {
    throw new Error('PDF base64 vazio');
  }

  return normalized;
}

function getPdfBase64(pdfGenerator) {
  return new Promise((resolve, reject) => {
    try {
      pdfGenerator.getBase64(base64 => {
        if (!base64) {
          reject(new Error('Erro ao gerar PDF em base64'));
          return;
        }
        resolve(normalizePdfBase64Input(base64));
      });
    } catch (error) {
      reject(error);
    }
  });
}

function pdfBase64ToBlob(pdfBase64) {
  const normalizedPdfBase64 = normalizePdfBase64Input(pdfBase64);
  const binary = atob(normalizedPdfBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: 'application/pdf' });
}

function resolveOfficialUploadProcedure(procedimento, officialConfig = {}) {
  if (officialConfig.uploadProcedimento) {
    return officialConfig.uploadProcedimento;
  }

  if (['receita', 'atestado', 'requisicao'].includes(procedimento)) {
    return 'prontuario';
  }

  return procedimento || 'prontuario';
}

async function uploadPdfAndResolveOfficialLink({
  codLaudo,
  fileName,
  pdfBlob,
  procedimento = 'ecg',
}) {
  if (!codLaudo) {
    throw new Error('codLaudo é obrigatório para envio oficial');
  }

  if (!fileName) {
    throw new Error('fileName é obrigatório para envio oficial');
  }

  const presignData = await uploadBlobToLaudo({
    procedimento,
    codLaudo,
    fileName,
    body: pdfBlob,
    contentType: 'application/pdf',
  });

  try {
    const { data: directGetData } = await requestLaudoPresignDownload({
      key: presignData.key,
    });
    const directGetLink = directGetData && directGetData.preSignedUrl;
    if (directGetLink) {
      return directGetLink;
    }
  } catch (_) {
    // Fallback seguinte
  }

  try {
    const { data: fileList } = await api.get('/files', {
      params: { codLaudo, procedimento },
    });

    const sentPdfName = String(fileName).toLowerCase();
    const pdfFile = (fileList || []).find(file =>
      String((file && file.Key) || '')
        .toLowerCase()
        .endsWith(sentPdfName)
    );

    if (pdfFile && pdfFile.Key) {
      const { data: getUrlData } = await requestLaudoPresignDownload({
        key: pdfFile.Key,
      });
      const fallbackLink = getUrlData && getUrlData.preSignedUrl;
      if (fallbackLink) {
        return fallbackLink;
      }
    }
  } catch (_) {
    // sem fallback adicional
  }

  throw new Error(
    'Não foi possível obter o link público/temporário do PDF para envio'
  );
}

// pra que essa funcao aqui? pq nao usa direto a outra?
async function sendPdfMessage({
  to,
  pdfBase64,
  paciente,
  codLaudo,
  fileName,
  caption,
}) {
  const response = await api.post('/instance/pdf-message', {
    to,
    pdfBase64: normalizePdfBase64Input(pdfBase64),
    paciente,
    codLaudo,
    fileName,
    caption,
  });

  return response.data;
}

function shouldFallbackToOfficialApi(error) {
  const httpStatus = error?.response?.status;

  // Network/CORS/timeout without HTTP response.
  if (!httpStatus) {
    return true;
  }

  return [408, 429, 500, 502, 503, 504].includes(httpStatus);
}

async function sendPdfMessageWithFallback({
  to,
  pdfBase64,
  paciente,
  codLaudo,
  fileName,
  caption,
  enableOfficialFallback = true,
  shouldFallback = shouldFallbackToOfficialApi,
  officialConfig = {},
}) {
  try {
    const evolutionResponse = await sendPdfMessage({
      to,
      pdfBase64,
      paciente,
      codLaudo,
      fileName,
      caption,
    });

    return {
      ...evolutionResponse,
      provider: 'evolution',
      fallbackUsed: false,
    };
  } catch (error) {
    const canFallback =
      enableOfficialFallback &&
      (typeof shouldFallback === 'function' ? shouldFallback(error) : false);

    if (!canFallback) {
      throw error;
    }

    const {
      bodyParameters,
      wabaId = 'ICM',
      procedimento = 'ecg',
      templateName = 'documento_clinico',
      languageCode = 'pt_BR',
      documentTitle = 'Documento clínico',
      dateLocale = 'pt-BR',
    } = officialConfig;
    const uploadProcedimento = resolveOfficialUploadProcedure(
      procedimento,
      officialConfig
    );

    const resolvedBodyParameters =
      Array.isArray(bodyParameters) && bodyParameters.length > 0
        ? bodyParameters
        : [
            paciente?.nome || '',
            documentTitle,
            new Date().toLocaleDateString(dateLocale),
          ];

    const pdfBlob = pdfBase64ToBlob(pdfBase64);
    const documentLink = await uploadPdfAndResolveOfficialLink({
      codLaudo,
      fileName,
      pdfBlob,
      procedimento: uploadProcedimento,
    });

    const officialResponse = await sendOfficialDocumentTemplateMessage({
      wabaId,
      to,
      documentLink,
      filename: fileName,
      templateName,
      languageCode,
      bodyParameters: resolvedBodyParameters,
    });

    return {
      ...officialResponse,
      message:
        officialResponse?.message || 'PDF enviado com sucesso pela API oficial',
      provider: 'official',
      fallbackUsed: true,
      evolutionError:
        error?.response?.data?.error || error?.message || 'Falha no Evolution',
    };
  }
}

async function enviarArquivo({
  pdfGenerator = null,
  pdfBase64 = null,
  to,
  paciente,
  codLaudo,
  fileName,
  caption,
  enableOfficialFallback = true,
  shouldFallback = shouldFallbackToOfficialApi,
  officialConfig = {},
}) {
  const resolvedPdfBase64 =
    typeof pdfBase64 === 'string' && pdfBase64.trim()
      ? pdfBase64
      : pdfGenerator
        ? await getPdfBase64(pdfGenerator)
        : '';

  if (!resolvedPdfBase64) {
    throw new Error('PDF não informado para envio');
  }

  return sendPdfMessageWithFallback({
    to,
    pdfBase64: resolvedPdfBase64,
    paciente,
    codLaudo,
    fileName,
    caption,
    enableOfficialFallback,
    shouldFallback,
    officialConfig,
  });
}

// PAI: rever esses parametros aqui. os que sao default, pq nao coloca direto sem ser argumento??
async function sendOfficialDocumentTemplateMessage({
  wabaId = 'ICM',
  to,
  documentLink,
  filename,
  templateName = 'documento_clinico',
  languageCode = 'pt_BR',
  bodyParameters = [],
}) {
  const belApiBaseUrl = process.env.REACT_APP_BEL_API_URL;
  const configuredPath = '/api/documento-clinico';

  if (!belApiBaseUrl) {
    throw new Error('URL da API oficial não configurada no ambiente');
  }

  const payload = {
    wabaId,
    to,
    documentLink,
    filename,
    templateName,
    languageCode,
    bodyParameters,
  };

  const normalizedBaseUrl = belApiBaseUrl.replace(/\/$/, '');
  const primaryUrl = `${normalizedBaseUrl}${configuredPath}`;

  const response = await api.post(primaryUrl, payload);
  return response.data;
}

async function sendPdfFromGenerator({
  pdfGenerator,
  to,
  paciente,
  codLaudo,
  fileName,
  caption,
}) {
  const pdfBase64 = await getPdfBase64(pdfGenerator);

  return sendPdfMessage({
    to,
    pdfBase64,
    paciente,
    codLaudo,
    fileName,
    caption,
  });
}

// PAI: rever esses parametros aqui. os que sao default, pq nao coloca direto sem ser argumento??
async function sendOfficialDocumentFromGenerator({
  pdfGenerator,
  to,
  codLaudo,
  fileName,
  bodyParameters = [],
  wabaId = 'ICM',
  procedimento = 'ecg',
  templateName = 'documento_clinico',
  languageCode = 'pt_BR',
}) {
  const pdfBase64 = await getPdfBase64(pdfGenerator);
  const pdfBlob = pdfBase64ToBlob(pdfBase64);

  const documentLink = await uploadPdfAndResolveOfficialLink({
    codLaudo,
    fileName,
    pdfBlob,
    procedimento,
  });

  return sendOfficialDocumentTemplateMessage({
    wabaId,
    to,
    documentLink,
    filename: fileName,
    templateName,
    languageCode,
    bodyParameters,
  });
}

export {
  normalizeWhatsappPhone,
  getPdfBase64,
  pdfBase64ToBlob,
  resolveOfficialUploadProcedure,
  uploadPdfAndResolveOfficialLink,
  sendOfficialDocumentFromGenerator,
  sendPdfMessage,
  sendPdfFromGenerator,
  sendPdfMessageWithFallback,
  enviarArquivo,
  shouldFallbackToOfficialApi,
  sendOfficialDocumentTemplateMessage,
};
